// @coachbrain/ui — shared, phone-first primitives that encode the design-language rules
// (BUILD_PLAN §7.1). Components consume CSS variables from @coachbrain/design-tokens/css.
import type { CSSProperties, ReactNode } from "react";
import type { Layer, Supervision } from "@coachbrain/contracts";

type Div = { children?: ReactNode; className?: string; style?: CSSProperties };

/** Renders ONLY for interpretation-layer content (R-OVR-02). The only text-bearing use of violet. */
export function LayerChip({ layer, label = "INTERPRETATION" }: { layer: Layer | undefined; label?: string }) {
  if (layer !== "interpretation") return null;
  return <span className="cb-chip cb-chip-violet" data-layer="interpretation">{label}</span>;
}

/** Measured, comparable numerals: blue, tabular (R-VIS-02). */
export function Measured({ children, className = "", strong = true, style }: Div & { strong?: boolean }) {
  return <span className={`cb-num cb-measured ${strong ? "cb-strong" : ""} ${className}`} style={style}>{children}</span>;
}
/** Numerals in navy (facts that are not comparable trend material). */
export function Num({ children, className = "", style }: Div) {
  return <span className={`cb-num cb-strong ${className}`} style={style}>{children}</span>;
}

export function Approved({ children = "Approved" }: { children?: ReactNode }) {
  return <span className="cb-badge cb-badge-approved">{children}</span>;
}
export function Unresolved({ children = "Unresolved" }: { children?: ReactNode }) {
  return <span className="cb-badge cb-badge-unresolved">{children}</span>;
}
export function SelfReport({ children = "Self-report" }: { children?: ReactNode }) {
  return <span className="cb-badge cb-badge-unresolved">{children}</span>;
}
export function SupervisionBadge({ supervision, status }: { supervision: Supervision; status: string }) {
  if (supervision === "unsupervised_self_report") return <SelfReport />;
  if (status === "approved") return <Approved />;
  if (status === "acknowledged") return <SelfReport />;
  return <Unresolved />;
}

export type Tone = "plain" | "goal" | "provisional" | "unresolved" | "measured" | "mint";
export function Card({ children, tone = "plain", className = "", accent, style, onClick }: Div & { tone?: Tone; accent?: "unresolved" | "navy" | "emerald"; onClick?: () => void }) {
  const cls = `cb-card cb-card-${tone} ${accent ? `cb-accent-${accent}` : ""} ${className}`;
  return onClick ? (
    <button type="button" className={`${cls} cb-card-button`} style={style} onClick={onClick}>{children}</button>
  ) : (
    <section className={cls} style={style}>{children}</section>
  );
}

export function SectionLabel({ children, tone = "label", right, className = "" }: Div & { tone?: "label" | "emerald" | "violet" | "unresolved" | "blue"; right?: ReactNode }) {
  return (
    <div className={`cb-section-label-row ${className}`}>
      <span className={`cb-section-label cb-label-${tone}`}>{children}</span>
      {right !== undefined ? <span className="cb-section-label-right">{right}</span> : null}
    </div>
  );
}

export function Chip({ children, tone = "measured", onClick, selected, ariaLabel }: { children: ReactNode; tone?: "measured" | "unresolved" | "violet" | "navy" | "ground"; onClick?: () => void; selected?: boolean; ariaLabel?: string }) {
  const cls = `cb-chip cb-chip-${tone} ${selected ? "cb-chip-selected" : ""}`;
  return onClick ? <button type="button" className={cls} onClick={onClick} aria-label={ariaLabel} aria-pressed={selected}>{children}</button> : <span className={cls}>{children}</span>;
}

/** Tap-answer buttons: first is filled navy when `primaryFirst`. */
export function TapAnswers({ options, onPick, primaryFirst = true, picked }: { options: string[]; onPick: (o: string, i: number) => void; primaryFirst?: boolean; picked?: string | null }) {
  return (
    <div className="cb-tap-row" role="group">
      {options.map((o, i) => (
        <button key={o} type="button" className={`cb-tap ${(picked ? picked === o : primaryFirst && i === 0) ? "cb-tap-primary" : ""}`} onClick={() => onPick(o, i)} aria-pressed={picked === o}>{o}</button>
      ))}
    </div>
  );
}

export function PrimaryButton({ children, onClick, tone = "emerald", type = "button", disabled, className = "", full }: { children: ReactNode; onClick?: () => void; tone?: "emerald" | "navy" | "outline"; type?: "button" | "submit"; disabled?: boolean; className?: string; full?: boolean }) {
  return <button type={type} disabled={disabled} className={`cb-btn cb-btn-${tone} ${full ? "cb-btn-full" : ""} ${className}`} onClick={onClick}>{children}</button>;
}

export function ConfidenceDots({ level, label }: { level: "low" | "moderate" | "high"; label?: string }) {
  const filled = level === "high" ? 5 : level === "moderate" ? 3 : 1;
  return (
    <div className="cb-conf" role="img" aria-label={`${label ?? level} confidence`}>
      {Array.from({ length: 5 }, (_, i) => <span key={i} className={`cb-conf-seg ${i < filled ? "cb-conf-on" : ""}`} />)}
      <span className="cb-conf-label">{label ?? (level === "moderate" ? "Moderate" : level === "high" ? "High" : "Low")}</span>
    </div>
  );
}

export function ProgressBar({ fraction, label }: { fraction: number; label?: string }) {
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  return <div className="cb-progress" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100} aria-label={label}><div className="cb-progress-fill" style={{ width: `${pct}%` }} /></div>;
}

// ---- Trend chart: takes ONLY a ComparableSeries (R-DOM-07 / R-HIS-02) ----
export type ComparablePoint = { id: string; label: string; value: number; kind: "tested" | "self_report" };
export type DetachedPoint = { id: string; label: string; value: number; legend: string };
export interface ComparableSeries {
  readonly comparableFor: "top_set_load" | "reps_at_load" | "time" | "distance" | "est_1rm";
  readonly exerciseName: string;
  readonly unit: string;
  readonly range: string;
  readonly points: readonly ComparablePoint[];
  /** Related work, never joined: drawn as detached, labeled marks. */
  readonly detached: readonly DetachedPoint[];
  readonly selfReportLegend?: string;
}

export function TrendChart({ series }: { series: ComparableSeries }) {
  const max = Math.max(...series.points.map((p) => p.value), ...series.detached.map((d) => d.value), 1);
  const items: Array<{ key: string; label: string; value: number; kind: "tested" | "self_report" | "detached" }> = [
    ...series.points.map((p) => ({ key: p.id, label: p.label, value: p.value, kind: p.kind })),
  ];
  // insert detached points before the last two comparable points to mirror the design's placement
  const insertAt = Math.max(0, items.length - 2);
  for (const d of series.detached) items.splice(insertAt, 0, { key: d.id, label: d.label, value: d.value, kind: "detached" });
  const selfReport = series.points.find((p) => p.kind === "self_report");
  return (
    <div className="cb-trend">
      <div className="cb-trend-head"><span className="cb-strong">{series.exerciseName === "" ? "Top working set" : `Top working set · ${series.unit}`}</span><span className="cb-muted">{series.range}</span></div>
      <div className="cb-trend-bars" role="img" aria-label={`${series.exerciseName} top working set, ${series.unit}`}>
        {items.map((it) => (
          <div key={it.key} className="cb-trend-col">
            {it.kind === "detached" ? (
              <>
                <span className="cb-num cb-trend-val cb-muted" style={{ position: "absolute", bottom: `calc(${(it.value / max) * 100}% + 6px)` }}>{it.value}</span>
                <span className="cb-trend-detached" style={{ bottom: `${(it.value / max) * 100}%` }} />
              </>
            ) : (<>
            <span className={`cb-num cb-trend-val ${it.kind === "self_report" ? "cb-text-unresolved" : ""}`}>{it.value}</span>
              <span className={`cb-trend-bar ${it.kind === "self_report" ? "cb-bar-unresolved" : "cb-bar-blue"}`} style={{ height: `${(it.value / max) * 100}%` }} />
            </>)}
          </div>
        ))}
      </div>
      <ul className="cb-legend">
        <li><span className="cb-legend-swatch cb-bar-blue" />Supervised, tested — comparable</li>
        {selfReport ? <li className="cb-text-unresolved cb-strong"><span className="cb-legend-swatch cb-bar-unresolved" />{series.selfReportLegend ?? `Self-report, ${selfReport.label} — weighted down`}</li> : null}
        {series.detached.map((d) => <li key={d.id}><span className="cb-legend-line" />{d.legend}</li>)}
      </ul>
    </div>
  );
}

// ---- App chrome ----
// One DOM, two layouts (R-CLI-01 mobile-first, R-CLI-03 desktop depth): below 768px the phone chrome
// (navy header + bottom nav) renders; from 768px (iPad portrait) a persistent left rail, a sticky top bar
// and a wide content area take over. Pure CSS media queries — no user-agent sniffing.
export type NavItem = { label: string; href: string; active?: boolean; icon?: ReactNode };
export function AppShell({ title, subtitle, right, actions, tabs, nav, children, wide, narrow, statusText = "9:41", onNavigate, chrome = "navy", railBrand, railFooter, hideNav }: {
  title?: ReactNode; subtitle?: ReactNode; right?: ReactNode; actions?: ReactNode; tabs?: ReactNode; nav?: NavItem[]; children: ReactNode; wide?: boolean; narrow?: boolean; statusText?: string; onNavigate?: (href: string) => void; chrome?: "navy" | "emerald"; railBrand?: ReactNode; railFooter?: ReactNode; hideNav?: boolean;
}) {
  const items = nav ?? [];
  const link = (n: NavItem, cls: string) => (
    <a key={n.href} href={n.href} className={`${cls} ${n.active ? "cb-nav-active" : ""}`} aria-current={n.active ? "page" : undefined} title={n.label} onClick={(e) => { if (onNavigate) { e.preventDefault(); onNavigate(n.href); } }}>
      {cls === "cb-rail-link" ? <span className="cb-rail-icon" aria-hidden="true">{n.icon ?? n.label.slice(0, 1)}</span> : null}
      <span className="cb-rail-label">{n.label}</span>
    </a>
  );
  return (
    <div className={`cb-app ${wide ? "cb-app-wide" : ""} ${narrow ? "cb-app-narrow" : ""} ${chrome === "emerald" ? "cb-app-emerald" : ""}`}>
      <nav className="cb-rail" aria-label="Primary">
        <div className="cb-rail-brand">{railBrand ?? "Coach Brain"}</div>
        <div className="cb-rail-items">{items.map((n) => link(n, "cb-rail-link"))}</div>
        {railFooter ? <div className="cb-rail-footer">{railFooter}</div> : null}
      </nav>
      <div className="cb-shell">
        <header className={`cb-chrome ${chrome === "emerald" ? "cb-chrome-emerald" : ""}`}>
          <div className="cb-status"><span>{statusText}</span><span className="cb-status-right">{right}</span></div>
          <div className="cb-title-row">
            {title !== undefined ? <div><h1 className="cb-title">{title}</h1>{subtitle ? <div className="cb-subtitle">{subtitle}</div> : null}</div> : <div />}
            <div className="cb-topbar-end">
              <div className="cb-topbar-right">{right}</div>
              {actions ? <div className="cb-topbar-actions">{actions}</div> : null}
            </div>
          </div>
          {tabs}
        </header>
        <main className="cb-main">{children}</main>
        {items.length && !hideNav ? <nav className="cb-nav" aria-label="Primary">{items.map((n) => link(n, ""))}</nav> : null}
      </div>
    </div>
  );
}
/** @deprecated alias — use AppShell. */
export const PhoneShell = AppShell;

/** Responsive column layouts. Single column on phones; the named grid from 768px / 1024px. */
export type ColumnsKind = "two" | "main-aside" | "overview" | "master-detail" | "cards";
export function Columns({ kind, children, className = "" }: { kind: ColumnsKind; children: ReactNode; className?: string }) {
  return <div className={`cb-cols cb-cols-${kind} ${className}`}>{children}</div>;
}
export function Col({ area, children, className = "" }: { area: "left" | "right" | "aside" | "main" | "list" | "detail"; children: ReactNode; className?: string }) {
  return <div className={`cb-col cb-col-${area} ${className}`}>{children}</div>;
}
/** Shown only from 768px (e.g. the always-visible challenge column). */
export function DesktopOnly({ children, className = "" }: Div) { return <div className={`cb-desktop-only ${className}`}>{children}</div>; }
export function PhoneOnly({ children, className = "" }: Div) { return <div className={`cb-phone-only ${className}`}>{children}</div>; }

export function Row({ left, right, sub, muted }: { left: ReactNode; right?: ReactNode; sub?: ReactNode; muted?: boolean }) {
  return (
    <div className={`cb-row ${muted ? "cb-muted" : ""}`}>
      <div className="cb-row-main"><span className="cb-row-left">{left}</span>{right !== undefined ? <span className="cb-row-right">{right}</span> : null}</div>
      {sub ? <div className="cb-row-sub">{sub}</div> : null}
    </div>
  );
}

export function Divider() { return <hr className="cb-divider" />; }
export function Muted({ children, className = "" }: Div) { return <span className={`cb-muted ${className}`}>{children}</span>; }
export function Strong({ children, className = "" }: Div) { return <span className={`cb-strong ${className}`}>{children}</span>; }
